import { z } from 'zod';
import { TransformationSchemaField } from '@/features/adp/services/common/common.types';
import { DATA_TYPES, NUMERIC_DATA_TYPES } from '@/features/adp/types/data.types';
import type { DynamicDataSources } from './TransformationFields';

function toCapitalizedLabel(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// ─── Static fallback options ──────────────────────────────────────────────────
// tables_list and columns_based_on_table are intentionally absent — they must
export const STATIC_DATA_SOURCES: Record<string, { label: string; value: string }[]> = {
  data_types_list: DATA_TYPES.map(dataType => ({
    label: toCapitalizedLabel(dataType),
    value: dataType,
  })),
  numeric_data_types_list: NUMERIC_DATA_TYPES.map(dataType => ({
    label: toCapitalizedLabel(dataType),
    value: dataType,
  })),
  join_type_list: [
    { label: 'Inner Join', value: 'inner' },
    { label: 'Left Join', value: 'left' },
    { label: 'Right Join', value: 'right' },
    { label: 'Full Outer Join', value: 'full_outer' },
  ],
};

/**
 * Resolve options for a dataSource key.
 * Dynamic overrides always win; static fallback is used for everything else.
 * Returns [] when neither has an entry (e.g. tables_list with no upstream source).
 */
export function getDataSourceOptions(
  dataSource?: string,
  dynamicDataSources?: DynamicDataSources
) {
  if (!dataSource) return [];
  return (
    dynamicDataSources?.[dataSource] ??
    STATIC_DATA_SOURCES[dataSource] ??
    []
  );
}

const INPUT_LITERAL_DATA_SOURCES = new Set([
  'int',
  'float',
  'double',
  'bigint',
  'string',
  'boolean',
  'date',
  'timestamp',
]);

export function resolveInputDefaultValue(field: TransformationSchemaField): string {
  if (typeof field.defaultValue === 'string') return field.defaultValue;
  const dataSource = field.dataSource?.trim();
  if (!dataSource) return '';
  return INPUT_LITERAL_DATA_SOURCES.has(dataSource.toLowerCase()) ? dataSource : '';
}

/**
 * Read a value from react-hook-form watch() output using dot paths and numeric
 * segments for field-array indices (e.g. aggregateList.0.aggregateType).
 */
export function getFormValueAtPath(root: unknown, path: string): unknown {
  if (!path) return undefined;
  const segments = path.split('.').filter(Boolean);
  let cur: unknown = root;
  for (const segment of segments) {
    if (cur == null || typeof cur !== 'object') return undefined;
    const asIndex = /^\d+$/.test(segment) ? Number(segment) : null;
    if (asIndex !== null && Array.isArray(cur)) {
      cur = cur[asIndex];
    } else {
      cur = (cur as Record<string, unknown>)[segment];
    }
  }
  return cur;
}

export function isFieldVisible(
  field: TransformationSchemaField,
  watchValues: Record<string, unknown>,
  /** RHF name prefix for nested fields, e.g. aggregateList.0. */
  namePrefix = ''
): boolean {
  const condition = field.visibleWhen ?? field.visibleIf;
  if (!condition) return true;
  const base = namePrefix.replace(/\.$/, '');
  const path = base ? `${base}.${condition.field}` : condition.field;
  return getFormValueAtPath(watchValues, path) === condition.equals;
}

function syncRowLookupKey(value: unknown): string {
  if (typeof value === 'string') return value.toLowerCase();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).toLowerCase();
  return '';
}

/** One empty object row for a repeatable `group` field (used by reset + field array UI). */
export function buildDefaultRowFromGroupSubfields(
  subfields: TransformationSchemaField[],
): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const f of subfields) {
    if (f.type === 'multiselect') row[f.key] = [];
    else if (f.type === 'input') row[f.key] = resolveInputDefaultValue(f);
    else row[f.key] = f.defaultValue ?? '';
  }
  return row;
}

/**
 * Builds the field-array value for a `group` from schema hints (sync with data
 * source, initial row count, or empty).
 */
export function buildGroupFieldArrayValue(
  field: TransformationSchemaField,
  currentValues: Record<string, unknown>,
  dynamicDataSources?: DynamicDataSources,
  /**
   * When true, non-key sub-fields (e.g. transpose `condition`) are copied from
   * `currentValues[field.key]` for matching rows. When false, each row starts
   * from defaults — needed after Add/Clear so the next entry is not pre-filled
   * from the submitted form.
   */
  mergeSyncedGroupRows = false,
): unknown[] {
  if (field.type !== 'group') return [];
  const subfields = field.fields ?? [];

  if (field.syncRowsFromDataSource) {
    const options = getDataSourceOptions(field.syncRowsFromDataSource, dynamicDataSources);
    const valueKey = field.syncRowValueKey ?? 'columnName';
    const prev = mergeSyncedGroupRows ? currentValues[field.key] : undefined;
    const prevRows = Array.isArray(prev) ? prev : [];
    const prevBy = new Map(
      prevRows
        .filter((r): r is Record<string, unknown> => r != null && typeof r === 'object')
        .map(r => [syncRowLookupKey(r[valueKey]), r]),
    );
    return options.map(opt => {
      const row = buildDefaultRowFromGroupSubfields(subfields);
      row[valueKey] = opt.value;
      const old = prevBy.get(syncRowLookupKey(opt.value));
      if (old) {
        for (const sf of subfields) {
          if (sf.key === valueKey) continue;
          const v = old[sf.key];
          if (v !== undefined && v !== '') row[sf.key] = v;
        }
      }
      return row;
    });
  }

  const n = Math.max(field.initialRowCount ?? 0, field.minItems ?? 0);
  if (n > 0) {
    return Array.from({ length: n }, () => buildDefaultRowFromGroupSubfields(subfields));
  }
  return [];
}

export function buildZodSchema(
  fields: TransformationSchemaField[]
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fields) {
    if (field.type === 'group') {
      const subShape = buildZodSchema(field.fields ?? []);
      let arrSchema: z.ZodTypeAny = z.array(subShape);
      if (typeof field.minItems === 'number' && field.minItems > 0) {
        arrSchema = (arrSchema as z.ZodArray<z.ZodObject<Record<string, z.ZodTypeAny>>>).min(
          field.minItems,
          `At least ${field.minItems} row(s) required`,
        );
      }
      shape[field.key] = arrSchema.optional();
    } else if (field.type === 'multiselect') {
      shape[field.key] = field.required
        ? z.array(z.string()).min(1, 'Required')
        : z.array(z.string()).optional();
    } else {
      shape[field.key] = field.required
        ? z.string().min(1, 'This field is required')
        : z.string().optional();
    }
  }
  return z.object(shape);
}

export const normalizeString = (value: unknown): string =>
  typeof value === 'string' ? value : '';

export const normalizeStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    : [];

export const toFieldOptions = (fields: { fieldName: string }[]) =>
  fields.map(field => ({ label: field.fieldName, value: field.fieldName }));

export const parseLookupSourceId = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value);
  return Number.NaN;
};

export const SELECTED_COLUMNS_WITH_TYPES_KEY = 'SelectedColumnsWithTypes';

export interface AppliedTransformation {
  id: string;
  label: string;
  formData: Record<string, unknown>;
}

/**
 * Shape of each entry in `SelectedColumnsWithTypes` stored inside
 * `AppliedTransformation.formData` for transformations that add columns.
 * Persisted alongside the plain `LookUpGetColumns` string array so
 * downstream consumers (pipeline execution, column mapping) have type
 * info available without re-fetching the datasource.
 */
export interface LookupGetColumnWithType {
  name: string;
  dataType: string;
}

export interface AppliedTransformationBatch {
  abbreviation: string;
  items: AppliedTransformation[];
}

// ─── Live column set ──────────────────────────────────────────────────────────

/**
 * A lightweight snapshot of a single column as it exists at a given point in
 * the applied-transformation chain.
 */
export type ColumnSnapshot = { fieldName: string; dataType: string };

/**
 * Applies a single Aggregation (`AG`) item to the current column set.
 *
 * Rule (mirrors Angular `generateDynamicColumns` Aggregate case):
 * - Keep only the group-by columns (preserving original data types).
 * - Append each new aggregate column with its chosen data type.
 */
function applyAggregationToColumns(
  columns: ColumnSnapshot[],
  formData: Record<string, unknown>,
): ColumnSnapshot[] {
  const groupByColumns = Array.isArray(formData.groupByColumns)
    ? (formData.groupByColumns as unknown[]).filter(
      (c): c is string => typeof c === 'string' && c.trim().length > 0,
    )
    : [];

  const groupBySet = new Set(groupByColumns.map(c => c.toLowerCase()));
  const kept = columns.filter(col => groupBySet.has(col.fieldName.toLowerCase()));

  const rawAggList = Array.isArray(formData.aggregateList) ? formData.aggregateList : [];
  const newCols: ColumnSnapshot[] = (rawAggList as unknown[]).flatMap(row => {
    const r = row != null && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const fieldName = (typeof r.newColumnName === 'string' ? r.newColumnName : '').trim();
    const dataType = (typeof r.newDataType === 'string' ? r.newDataType : '').trim() || 'float';
    return fieldName ? [{ fieldName, dataType }] : [];
  });

  return [...kept, ...newCols];
}

/**
 * Applies a single Drop Fields (`DF`) item:
 * - removes every column listed in `dropFieldsList` (case-insensitive).
 */
function applyDropFieldsToColumns(
  columns: ColumnSnapshot[],
  formData: Record<string, unknown>,
): ColumnSnapshot[] {
  const dropped = Array.isArray(formData.dropFieldsList)
    ? (formData.dropFieldsList as unknown[])
      .filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
      .map(c => c.trim().toLowerCase())
    : [];

  if (dropped.length === 0) return columns;

  const droppedSet = new Set(dropped);
  return columns.filter(col => !droppedSet.has(col.fieldName.toLowerCase()));
}

/**
 * Applies a single Join (`JR`) item: appends the secondary's `joinTableColumns`
 * to the current column set (already excludes the secondary join key, since
 * that was filtered at save time by `JoinIntegrationSheet`).
 */
function applyJoinToColumns(
  columns: ColumnSnapshot[],
  formData: Record<string, unknown>,
): ColumnSnapshot[] {
  const raw =
    typeof formData.joinTableColumns === 'string' ? formData.joinTableColumns : '';
  if (!raw.trim()) return columns;

  // Parallel CSV of data types (added in the same save). May be absent on
  // older saved Join rows — fall back to '' so the downstream replay still
  // works, even if type-aware dropdowns are empty for those columns.
  const rawTypes =
    typeof formData.joinTableColumnTypes === 'string' ? formData.joinTableColumnTypes : '';
  const types = rawTypes.split(',').map((t) => t.trim());

  const existingNames = new Set(columns.map((c) => c.fieldName));
  const additions: ColumnSnapshot[] = [];
  raw
    .split(',')
    .map((name) => name.trim())
    .forEach((name, idx) => {
      if (!name || existingNames.has(name)) return;
      additions.push({ fieldName: name, dataType: types[idx] ?? '' });
    });

  return [...columns, ...additions];
}

function applyTransformationItemToColumns(
  columns: ColumnSnapshot[],
  item: AppliedTransformation,
  abbreviation: string,
): ColumnSnapshot[] {
  if (abbreviation === 'AG') {
    return applyAggregationToColumns(columns, item.formData);
  }
  if (abbreviation === 'DF') {
    return applyDropFieldsToColumns(columns, item.formData);
  }
  if (abbreviation === 'JR') {
    return applyJoinToColumns(columns, item.formData);
  }
  if (abbreviation === 'UN') {
    // Union stacks rows from compatible-schema sources; the output has the
    // primary's columns unchanged. Downstream transformations see the same
    // schema as before the Union, so no column changes here.
    return columns;
  }
  // Extend here for additional transformations (e.g. DC, RC).
  return columns;
}

/**
 * Replays transformation batches (upstream nodes + current node draft) against
 * the base column set in order, returning the "live" columns visible to the
 * current form.
 *
 * Mirrors Angular `generateDynamicColumns`: each applied item mutates the
 * current column set before the next item is processed.
 */
export function applyTransformationBatchesToColumns(
  baseColumns: ColumnSnapshot[],
  batches: AppliedTransformationBatch[],
): ColumnSnapshot[] {
  let columns = baseColumns;

  for (const batch of batches) {
    for (const item of batch.items) {
      columns = applyTransformationItemToColumns(columns, item, batch.abbreviation);
    }
  }

  return columns;
}

export type BuildPartialResetOptions = {
  /**
   * When true, synced groups merge row state from `currentValues` (e.g. keep
   * transpose conditions when rebuilding rows). Default false so Add/Clear
   * yields a fresh form; set true only if you intentionally preserve row data.
   */
  mergeSyncedGroupRows?: boolean;
};

export function buildPartialReset(
  fields: TransformationSchemaField[],
  currentValues: Record<string, unknown>,
  dynamicDataSources?: DynamicDataSources,
  resetOptions?: BuildPartialResetOptions,
): Record<string, unknown> {
  const mergeSyncedGroupRows = resetOptions?.mergeSyncedGroupRows ?? false;
  const result: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.dataSource === 'tables_list') {
      const tableOptions = getDataSourceOptions('tables_list', dynamicDataSources);
      result[field.key] =
        currentValues[field.key] ??
        field.defaultValue ??
        (tableOptions.length === 1 ? tableOptions[0].value : '');
    } else if (field.type === 'multiselect') {
      result[field.key] = [];
    } else if (field.type === 'group') {
      result[field.key] = buildGroupFieldArrayValue(
        field,
        currentValues,
        dynamicDataSources,
        mergeSyncedGroupRows,
      );
    } else {
      result[field.key] =
        field.type === 'input'
          ? resolveInputDefaultValue(field)
          : field.defaultValue ?? '';
    }
  }
  return result;
}
